import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, Waves, HeartHandshake, X, Send, CheckCircle } from 'lucide-react'
import { addDocument } from '../../services/firebase'

const QuickActions = () => {
  const [activeModal, setActiveModal] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    // Group specific
    preferredDay: '',
    // Baptism specific
    salvationDate: '',
    // Volunteer specific
    ministry: '',
    availability: '',
  })

  const actions = [
    {
      id: 'join-group',
      title: 'JOIN A GROUP',
      icon: Users,
      color: 'primary',
      bgGradient: 'from-primary-500 to-primary-600',
      hoverGradient: 'hover:from-primary-600 hover:to-primary-700',
    },
    {
      id: 'get-baptized',
      title: 'GET BAPTIZED',
      icon: Waves,
      color: 'secondary',
      bgGradient: 'from-secondary-500 to-secondary-600',
      hoverGradient: 'hover:from-secondary-600 hover:to-secondary-700',
    },
    {
      id: 'volunteer',
      title: 'VOLUNTEER / SERVE',
      icon: HeartHandshake,
      color: 'accent',
      bgGradient: 'from-accent-500 to-accent-600',
      hoverGradient: 'hover:from-accent-600 hover:to-accent-700',
    },
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
      preferredDay: '',
      salvationDate: '',
      ministry: '',
      availability: '',
    })
    setIsSuccess(false)
  }

  const handleSubmit = async (e, formType) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const submissionData = {
        name: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        formType,
        status: 'new',
        submittedAt: new Date().toISOString(),
      }

      // Add form-specific fields
      if (formType === 'join-group') {
        submissionData.preferredDay = formData.preferredDay
      } else if (formType === 'baptism') {
        submissionData.salvationDate = formData.salvationDate
      } else if (formType === 'volunteer') {
        submissionData.ministry = formData.ministry
        submissionData.availability = formData.availability
      }

      // Submit to Firebase
      await addDocument('formSubmissions', submissionData)

      setIsSuccess(true)
      setTimeout(() => {
        setActiveModal(null)
        resetForm()
      }, 2000)
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('There was an error submitting your form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeModal = () => {
    setActiveModal(null)
    resetForm()
  }

  const renderForm = () => {
    if (isSuccess) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          </motion.div>
          <h3 className="text-2xl font-heading font-bold text-dark-100 mb-2">
            Thank You!
          </h3>
          <p className="text-dark-300">
            Your submission has been received. We'll be in touch soon!
          </p>
        </motion.div>
      )
    }

    const inputClass = "w-full px-4 py-3 bg-light-100 border-2 border-transparent rounded-xl font-body transition-all duration-300 focus:outline-none focus:border-primary-400 focus:bg-white"

    if (activeModal === 'join-group') {
      return (
        <form onSubmit={(e) => handleSubmit(e, 'join-group')} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              name="firstName"
              placeholder="First Name *"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name *"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
          </div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleInputChange}
            required
            className={inputClass}
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleInputChange}
            className={inputClass}
          />
          <select
            name="preferredDay"
            value={formData.preferredDay}
            onChange={handleInputChange}
            required
            className={inputClass}
          >
            <option value="">Preferred Meeting Day *</option>
            <option value="sunday">Sunday</option>
            <option value="monday">Monday</option>
            <option value="tuesday">Tuesday</option>
            <option value="wednesday">Wednesday</option>
            <option value="thursday">Thursday</option>
            <option value="friday">Friday</option>
            <option value="saturday">Saturday</option>
          </select>
          <textarea
            name="message"
            placeholder="Tell us about yourself and what you're looking for in a small group..."
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
            className={inputClass}
          />
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? 'Submitting...' : 'Join A Group'}
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
      )
    }

    if (activeModal === 'get-baptized') {
      return (
        <form onSubmit={(e) => handleSubmit(e, 'baptism')} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              name="firstName"
              placeholder="First Name *"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name *"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
          </div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleInputChange}
            required
            className={inputClass}
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={handleInputChange}
            required
            className={inputClass}
          />
          <input
            type="date"
            name="salvationDate"
            placeholder="When did you accept Christ?"
            value={formData.salvationDate}
            onChange={handleInputChange}
            className={inputClass}
          />
          <textarea
            name="message"
            placeholder="Share your testimony or any questions you have about baptism..."
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
            className={inputClass}
          />
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? 'Submitting...' : 'Request Baptism'}
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
      )
    }

    if (activeModal === 'volunteer') {
      return (
        <form onSubmit={(e) => handleSubmit(e, 'volunteer')} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              name="firstName"
              placeholder="First Name *"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name *"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className={inputClass}
            />
          </div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleInputChange}
            required
            className={inputClass}
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleInputChange}
            className={inputClass}
          />
          <select
            name="ministry"
            value={formData.ministry}
            onChange={handleInputChange}
            required
            className={inputClass}
          >
            <option value="">Select Ministry Area *</option>
            <option value="worship">Worship & Music</option>
            <option value="children">Children's Ministry</option>
            <option value="youth">Youth Ministry</option>
            <option value="hospitality">Hospitality & Ushering</option>
            <option value="media">Media & Tech</option>
            <option value="outreach">Outreach & Evangelism</option>
            <option value="prayer">Prayer Ministry</option>
            <option value="other">Other</option>
          </select>
          <select
            name="availability"
            value={formData.availability}
            onChange={handleInputChange}
            required
            className={inputClass}
          >
            <option value="">Your Availability *</option>
            <option value="sundays">Sundays Only</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekends">Weekends</option>
            <option value="flexible">Flexible</option>
          </select>
          <textarea
            name="message"
            placeholder="Tell us about your skills, experience, or any questions..."
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
            className={inputClass}
          />
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? 'Submitting...' : 'Start Serving'}
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
      )
    }

    return null
  }

  const getModalTitle = () => {
    switch (activeModal) {
      case 'join-group':
        return { title: 'Join A Small Group', subtitle: 'Connect with others and grow in faith together' }
      case 'get-baptized':
        return { title: 'Request Baptism', subtitle: 'Take the next step in your faith journey' }
      case 'volunteer':
        return { title: 'Volunteer To Serve', subtitle: 'Use your gifts to make a difference' }
      default:
        return { title: '', subtitle: '' }
    }
  }

  const getModalColor = () => {
    switch (activeModal) {
      case 'join-group':
        return 'from-primary-500 to-primary-600'
      case 'get-baptized':
        return 'from-secondary-500 to-secondary-600'
      case 'volunteer':
        return 'from-accent-500 to-accent-600'
      default:
        return 'from-primary-500 to-primary-600'
    }
  }

  return (
    <>
      <section className="py-16 bg-gradient-to-b from-light-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {actions.map((action, index) => {
              const IconComponent = action.icon

              return (
                <motion.div
                  key={action.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <motion.button
                    onClick={() => setActiveModal(action.id)}
                    className={`
                      w-full bg-gradient-to-r ${action.bgGradient} ${action.hoverGradient}
                      relative overflow-hidden rounded-2xl p-10 md:p-12
                      flex flex-col items-center justify-center
                      transition-all duration-300 cursor-pointer
                      min-h-[160px] shadow-lg
                    `}
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Diagonal Lines Pattern */}
                    <div
                      className="absolute inset-0 opacity-10"
                      style={{
                        backgroundImage: `repeating-linear-gradient(
                          -45deg,
                          transparent,
                          transparent 10px,
                          rgba(255,255,255,0.1) 10px,
                          rgba(255,255,255,0.1) 20px
                        )`
                      }}
                    />

                    {/* Icon */}
                    <motion.div
                      className="mb-4 relative z-10"
                      whileHover={{ scale: 1.1 }}
                    >
                      <IconComponent className="w-10 h-10 text-white" strokeWidth={1.5} />
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-lg md:text-xl font-heading font-bold text-white tracking-wider relative z-10">
                      {action.title}
                    </h3>
                  </motion.button>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`bg-gradient-to-r ${getModalColor()} p-6 rounded-t-3xl relative`}>
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
                <h2 className="text-2xl font-heading font-bold text-white">
                  {getModalTitle().title}
                </h2>
                <p className="text-white/80 mt-1">
                  {getModalTitle().subtitle}
                </p>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {renderForm()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default QuickActions
